'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Check, Loader2 } from 'lucide-react';
import { VoiceAgentModal, type MomentoVoice } from '@/components/livekit/VoiceAgentModal';
import { useSupabaseAuth } from '@/lib/auth/simplified/SupabaseAuthContext';
import { toast } from 'sonner';

const MOMENTO_VOICES: Record<string, MomentoVoice> = {
  tessa: { id: 'Ashley', name: 'Tessa', description: 'Ready to share something great?', avatar: '/voices/tessa.png' },
};

const INTERVIEW_VOICE_IDS: Record<string, { id: string; name: string }> = {
  tessa: { id: 'Ashley', name: 'Tessa' },
};

const checkFeatures = [
  'Available 24/7, start in seconds',
  'Guided prompts that unlock insights',
  'AI-extracted moments, instantly',
];

const humanFeatures = [
  'Expert interviewers, not generic hosts',
  'Studio-quality recording',
  'Same AI moment detection + content',
];

interface GuideProfile {
  handle: string;
  name: string;
  avatar_url: string | null;
}

export default function TwoWaysSection() {
  const [activeVoice, setActiveVoice] = useState<MomentoVoice | null>(null);
  const [isStartingInterview, setIsStartingInterview] = useState(false);
  const [guides, setGuides] = useState<GuideProfile[]>([]);
  const auth = useSupabaseAuth();
  const router = useRouter();

  useEffect(() => {
    fetch('/api/public/guides')
      .then((res) => res.json())
      .then((data) => {
        if (data.guides) setGuides(data.guides.slice(0, 2));
      })
      .catch(() => {});
  }, []);

  const handleStartInterview = async () => {
    if (!auth.isAuthenticated) {
      setActiveVoice(MOMENTO_VOICES.tessa);
      return;
    }

    setIsStartingInterview(true);
    try {
      const voice = INTERVIEW_VOICE_IDS.tessa;
      const res = await fetch('/api/agent/interview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voiceId: voice.id, voiceName: voice.name }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to start');
      const data = await res.json();
      const params = new URLSearchParams({ token: data.token, serverUrl: data.serverUrl });
      router.push(`/rooms/${data.roomName}?${params.toString()}`);
    } catch (error) {
      console.error('[TwoWays] Interview error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to start conversation');
      setIsStartingInterview(false);
    }
  };

  return (
    <section className="py-20">
      <div className="max-w-[1200px] mx-auto px-8">
        <div className="text-center mb-13">
          <div className="landing-section-label">Choose your path</div>
          <h2 className="text-[38px] font-bold tracking-[-1.5px] mb-3.5 text-foreground">
            Two ways to start talking
          </h2>
          <p className="text-[17px] text-muted-foreground max-w-[540px] mx-auto">
            AI guide or human conversation partner — every session captures moments worth sharing.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* AI Guide Card */}
          <button
            onClick={handleStartInterview}
            disabled={isStartingInterview}
            className="bg-card border border-border rounded-[16px] p-10 text-left transition-all hover:border-border-hover hover:shadow-md hover:-translate-y-1 relative overflow-hidden group"
          >
            <div className="absolute top-[-80px] right-[-80px] w-[220px] h-[220px] bg-[conic-gradient(from_0deg,rgba(239,68,68,0.06),rgba(139,92,246,0.06),rgba(59,130,246,0.06),rgba(239,68,68,0.06))] rounded-full blur-[40px]" />

            <div className="w-full h-[100px] flex items-center justify-center mb-4 relative">
              <div
                className="w-[70px] h-[70px] rounded-full opacity-65"
                style={{
                  background: 'conic-gradient(from 0deg, #EF4444, #8B5CF6, #3B82F6, #EF4444)',
                  filter: 'blur(14px)',
                  animation: 'floatY 4s ease-in-out infinite',
                }}
              />
            </div>

            <div className="text-[11px] uppercase tracking-[1.5px] text-[#EF4444] mb-2.5 font-space-mono">
              Instant · No scheduling
            </div>
            <h3 className="text-[22px] font-bold tracking-[-0.3px] mb-2.5 text-foreground">
              Talk to an AI Guide
            </h3>
            <p className="text-[14px] text-muted-foreground leading-[1.7] mb-5">
              Our AI guides ask the right questions to draw out your best insights. Start right now — no calendar, no waiting.
            </p>
            <ul className="space-y-2 mb-6">
              {checkFeatures.map((f) => (
                <li key={f} className="flex items-start gap-2 text-[13px] text-foreground">
                  <Check className="w-3.5 h-3.5 text-[#10B981] flex-shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
            <span className="inline-flex items-center gap-2 bg-foreground text-background px-5 py-2.5 rounded-full text-[14px] font-semibold transition-all">
              {isStartingInterview ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Starting...
                </>
              ) : (
                'Start a Conversation →'
              )}
            </span>
          </button>

          {/* Human Guide Card */}
          <Link
            href="/home/guides"
            className="bg-card border border-border rounded-[16px] p-10 transition-all hover:border-border-hover hover:shadow-md hover:-translate-y-1 block"
          >
            <div className="flex mb-4">
              {guides.length > 0 ? (
                guides.map((guide, i) => (
                  <div
                    key={guide.handle}
                    className={`w-[52px] h-[52px] rounded-full border-2 border-background shadow-sm overflow-hidden ${i > 0 ? '-ml-2' : ''}`}
                  >
                    {guide.avatar_url ? (
                      <Image
                        src={guide.avatar_url}
                        alt={guide.name}
                        width={52}
                        height={52}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className={`w-full h-full flex items-center justify-center font-bold text-lg ${i === 0 ? 'bg-gradient-to-br from-[#DBEAFE] to-[#BFDBFE] text-[#3B82F6]' : 'bg-gradient-to-br from-[#D1FAE5] to-[#A7F3D0] text-[#059669]'}`}>
                        {guide.name.charAt(0)}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <>
                  <div className="w-[52px] h-[52px] rounded-full bg-gradient-to-br from-[#DBEAFE] to-[#BFDBFE] flex items-center justify-center text-[#3B82F6] font-bold text-lg border-2 border-background shadow-sm">
                    S
                  </div>
                  <div className="w-[52px] h-[52px] rounded-full bg-gradient-to-br from-[#D1FAE5] to-[#A7F3D0] flex items-center justify-center text-[#059669] font-bold text-lg border-2 border-background shadow-sm -ml-2">
                    C
                  </div>
                </>
              )}
            </div>

            <div className="text-[11px] uppercase tracking-[1.5px] text-[#3B82F6] mb-2.5 font-space-mono">
              Book a session
            </div>
            <h3 className="text-[22px] font-bold tracking-[-0.3px] mb-2.5 text-foreground">
              Talk with a Human Guide
            </h3>
            <p className="text-[14px] text-muted-foreground leading-[1.7] mb-5">
              Expert conversation guides who know how to draw out stories your audience will love. Real humans, real connection.
            </p>
            <ul className="space-y-2 mb-6">
              {humanFeatures.map((f) => (
                <li key={f} className="flex items-start gap-2 text-[13px] text-foreground">
                  <Check className="w-3.5 h-3.5 text-[#10B981] flex-shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
            <span className="inline-flex items-center gap-2 bg-background text-foreground border border-border px-5 py-2.5 rounded-full text-[14px] font-semibold">
              Browse Guides →
            </span>
          </Link>
        </div>
      </div>

      {activeVoice && (
        <VoiceAgentModal
          open={!!activeVoice}
          onOpenChange={(isOpen) => { if (!isOpen) setActiveVoice(null); }}
          voice={activeVoice}
        />
      )}
    </section>
  );
}
